/**
 * <LessonVideos /> — Cycle 55 · In-frame curated video carousel.
 * ─────────────────────────────────────────────────────────────
 * Fetches curated + approved videos from /api/topic-videos scoped to
 * this lesson/topic + tier and shows them as a tap-to-play carousel
 * that plays inline (in a modal via LessonVideo) — no external
 * navigation to YouTube search results.
 *
 * Feb 2026 — replaced the previous "opens YouTube search in new tab"
 * pattern per user feedback: children were getting pulled out of the
 * app into YouTube's recommended-video sidebar and never returning.
 */
import React, { useEffect, useState } from "react";
import { Stamp } from "@/components/paperAtlas/primitives";
import SafeVideoPlayer from "@/components/SafeVideoPlayer";
import { Play, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function LessonVideos({
  subject,
  lessonTitle,
  outcome,
  lessonCode,   // optional — preferred, uses lesson_topic_links
  topicId,      // optional — direct topic lookup fallback
  tier,         // "seedling" | "explorer" | "pioneer" — used to scope
}) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const url = new URL(`${API}/api/topic-videos`);
        if (lessonCode) url.searchParams.set("lesson_code", lessonCode);
        else if (topicId) url.searchParams.set("topic_id", topicId);
        if (subject) url.searchParams.set("subject", subject);
        if (tier) url.searchParams.set("tier", tier);
        url.searchParams.set("limit", "12");
        const r = await fetch(url.toString(), { signal: AbortSignal.timeout(4500) });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (cancel) return;
        // Only show approved videos on the kid surface. Curator marks
        // videos as "approved" once safety+relevance checks pass.
        setVideos((j.videos || []).filter((v) => v.status === "approved").slice(0, 8));
      } catch { if (!cancel) setVideos([]); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [lessonCode, topicId, subject, tier]);

  const activeVideo = openIdx != null ? videos[openIdx] : null;

  const hasAny = videos.length > 0;

  if (loading) {
    return (
      <section
        data-testid="lesson-videos-loading"
        className="rounded-2xl border-[3px] border-slate-900 dark:border-starlight-200 bg-paper-100 dark:bg-night-900 p-4 md:p-5 shadow-stamp"
      >
        <p className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-starlight-400">
          <Loader2 className="animate-spin" size={12} strokeWidth={3} /> Finding related videos…
        </p>
      </section>
    );
  }
  if (!hasAny) return null; // silently omit rather than dead-end

  return (
    <>
      <section
        data-testid="lesson-videos"
        className="rounded-2xl border-[3px] border-slate-900 dark:border-starlight-200 bg-paper-100 dark:bg-night-900 p-4 md:p-5 shadow-stamp"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-starlight-400">
              Related videos
            </p>
            <p className="font-heading font-bold text-slate-900 dark:text-starlight-50 text-base leading-tight mt-0.5">
              🎬 Similar approved videos
            </p>
          </div>
          <Stamp label="Kid-safe" tint="#4E8A55" angle={-2} />
        </div>
        <ul
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3"
          data-testid="lesson-videos-list"
        >
          {videos.map((v, i) => (
            <li key={v.id || `${v.youtube_id}-${i}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(i)}
                data-testid={`lesson-video-tile-${i}`}
                aria-label={`Play video: ${v.title}`}
                className="group relative w-full aspect-video rounded-xl overflow-hidden border-[2.5px] border-slate-900 dark:border-starlight-200 shadow-[0_3px_0_0_rgba(15,23,42,1)] dark:shadow-[0_3px_0_0_#26315C] bg-slate-900 focus:outline-none focus:ring-4 focus:ring-emerald-400"
                style={{
                  backgroundImage: `url(${v.thumbnail_url || `https://img.youtube.com/vi/${v.youtube_id || v.id}/hqdefault.jpg`})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <span aria-hidden className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-emerald-400 border-[3px] border-white shadow-[0_3px_0_0_rgba(15,23,42,0.6)] group-hover:scale-110 transition-transform">
                    <Play size={16} strokeWidth={3} className="text-slate-900 ml-0.5" />
                  </span>
                </span>
                <span className="absolute left-0 right-0 bottom-0 px-2 pb-1.5 pt-3 text-[10px] font-bold text-white bg-gradient-to-t from-black/80 to-transparent line-clamp-2 text-left">
                  {v.title || "Untitled"}
                </span>
                {/* Teacher-submitted badge — shown when this video was
                    submitted through the Real Teacher portal. Signals
                    to kids + parents that a human tutor picked this
                    resource. */}
                {v.source === "teacher" && (
                  <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 h-5 px-1.5 rounded-full border border-slate-900 bg-fuchsia-300 text-[9px] font-black uppercase tracking-widest shadow-sm">
                    {v.submitter_avatar || "🧑‍🏫"} Teacher
                  </span>
                )}
              </button>
              {v.source === "teacher" && v.submitted_by_teacher_name && (
                <p className="mt-1 text-[10px] font-bold text-slate-500 truncate">
                  by {v.submitted_by_teacher_name}
                </p>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[10px] font-bold text-slate-500 dark:text-starlight-400">
          All videos play here in the app — nothing opens on YouTube.
        </p>
      </section>

      {/* In-frame player modal. Uses the same LessonVideo shell so
          Reader-mode transcripts + progress-preservation come for free. */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[10000] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="lesson-video-modal"
          onClick={() => setOpenIdx(null)}
          role="dialog"
          aria-modal="true"
          aria-label={activeVideo.title}
        >
          <div
            className="w-full max-w-3xl bg-white dark:bg-night-900 rounded-2xl border-[3px] border-slate-900 dark:border-starlight-200 overflow-hidden shadow-[0_8px_0_0_rgba(15,23,42,1)] dark:shadow-[0_8px_0_0_#26315C]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b-2 border-slate-900 dark:border-starlight-200 bg-yellow-100 dark:bg-night-800">
              <p className="font-bold text-sm text-slate-900 dark:text-starlight-50 truncate pr-3">{activeVideo.title}</p>
              <button
                type="button"
                onClick={() => setOpenIdx(null)}
                data-testid="lesson-video-modal-close"
                aria-label="Close video"
                className="inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-slate-900 bg-white text-slate-900 font-black shrink-0"
              >
                ×
              </button>
            </div>
            <SafeVideoPlayer
              video={{
                id: activeVideo.youtube_id || activeVideo.id,
                title: activeVideo.title,
                poster: activeVideo.thumbnail_url,
                provider: activeVideo.provider || "youtube",
                src: activeVideo.src,
              }}
              autoPlay
              testId="lesson-videos-modal-player"
            />
          </div>
        </div>
      )}
    </>
  );
}
